import { createAction, createReducer } from 'redux-act';
import { API_INSTANCE } from '../../utils/service-http';
import ProductInformationCache from '../../persistence/description-cache';
import { getAppLocale } from '../../utils/app-locale';
import { api_base_url } from '../../constants';
import ProductCategory from '../../models/ProductCategory';
import Validator from '../../models/Validator';
import { appConfiguration } from '../../utils/app-starter';

/**
 * Reducer and action used to get the head devices from AWS.
 */

const DEFAULT_STATE_HEAD_MODULES = { ranges: [], isLoading: false, error: null };

export const getHeadModulesStarted = createAction('GET_HEAD_MODULES_STARTED');
export const getHeadModulesSuccess = createAction('GET_HEAD_MODULES_SUCCESS');
export const getHeadModulesFailed = createAction('GET_HEAD_MODULES_FAILED');

export const getHeadModulesReducer = createReducer(function (on, off) {
  on(getHeadModulesStarted, (state, payload) => {
    return {
      ranges: [],
      isLoading: true,
      error: null,
    }
  });
  on(getHeadModulesSuccess, (state, payload) => {
    return {
      ranges: payload,
      isLoading: false,
      error: null,
    }
  });
  on(getHeadModulesFailed, (state, payload) => {
    return {
      ranges: [],
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE_HEAD_MODULES);

export const getHeadModules = (headModuleType, criteria) => {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return async (dispatch, getState) => {
    dispatch(getHeadModulesStarted());
    const payload = {
      criteria: { ...criteria, },
      distributorUnscoping: appConfiguration.distributorUnscoping
    }

    const rangesToScope = getState().callByRangeReducer?.selectedItems.ranges ? Object.keys(getState().callByRangeReducer?.selectedItems.ranges) : [];
    const referencesToScope = getState().callByRangeReducer?.selectedItems.references ? Object.keys(getState().callByRangeReducer?.selectedItems.references) : [];

    if (rangesToScope.length > 0) {
      payload.scopedCategories = rangesToScope;
    }

    if (getState().callByRangeReducer?.selectedItems.references) {
      payload.scopedReferences = referencesToScope;

    }

    return API_INSTANCE.post(`${api_base_url}/references?rangeType=${headModuleType}&locale=${getAppLocale()}${mainOverrideString}`, payload)
      .then((success) => {
        // Update description
        const allReferences = [];
        const ranges = success.data.payload.ranges;
        for (let range of ranges) {
          allReferences.push(...range.references);
        }
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));
        dispatch(getHeadModulesSuccess(ranges));
        return success.data.payload;
      }).catch((error) => {
        dispatch(getHeadModulesFailed(error.message));
        throw error;
      });
  }
}

/* Next IO modules, COM modules and cartridges retrieval */
const DEFAULT_STATE_COMPATIBLE_MODULES = {
  ioModuleCategories: [],
  comModuleCategories: [],
  cartridgeCategories: [],
  ioModulesFailedRules: [],
  comModulesFailedRules: [],
  cartridgesFailedRules: [],
  ioModulesRemainingCriteria: {},
  comModulesRemainingCriteria: {},
  ioModulesSharedOptions: {},
  comModulesSharedOptions: {},
  cartridgesRemainingCriteria: {},
  ioModulesDevicesFailedRules: {},
  comModulesDevicesFailedRules: {},
  cartridgesDevicesFailedRules: {},
  canAddExtensionRack: false,
  isLoading: false,
  error: null,
};

const onCompatibleModulesStarted = (state, payload) => {
  return {
    ...state,
    isLoading: true,
    error: null,
  }
};
const onCompatibleModulesSuccess = (state, payload) => {
  const ioModuleCategories = (payload?.ioModules?.categories || []).map(cData => new ProductCategory(cData || {}));
  const comModuleCategories = (payload?.comModules?.categories || []).map(cData => new ProductCategory(cData || {}));
  const cartridgeCategories = (payload?.cartridges?.categories || []).map(cData => new ProductCategory(cData || {}));
  return {
    ...state,
    ioModuleCategories,
    ioModulesFailedRules: new Validator(payload?.ioModules?.futureConfigValidator || {}).failedRules,
    ioModulesRemainingCriteria: payload?.ioModules?.remainingCriteria || {},
    ioModulesDevicesFailedRules: ioModuleCategories.map(c => c.failedRulesForReferences).reduce((previous, current) => {
      return { ...previous, ...current };
    }, {}),
    comModuleCategories,
    comModulesFailedRules: new Validator(payload?.comModules?.futureConfigValidator || {}).failedRules,
    comModulesRemainingCriteria: payload?.comModules?.remainingCriteria || {},
    ioModulesSharedOptions: payload?.ioModules?.sharedCriteria || {},
    comModulesSharedOptions: payload?.comModules?.sharedCriteria || {},
    comModulesDevicesFailedRules: comModuleCategories.map(c => c.failedRulesForReferences).reduce((previous, current) => {
      return { ...previous, ...current };
    }, {}),
    cartridgeCategories,
    cartridgesFailedRules: new Validator(payload?.cartridges?.futureConfigValidator || {}).failedRules,
    cartridgesRemainingCriteria: payload?.cartridges?.remainingCriteria || {},
    cartridgesDevicesFailedRules: cartridgeCategories.map(c => c.failedRulesForReferences).reduce((previous, current) => {
      return { ...previous, ...current };
    }, {}),
    canAddExtensionRack: payload?.canAddExtensionRack,
    isLoading: false,
    error: null,
  }
};
const onCompatibleModulesSuccessWithoutUpdate = (state, payload) => {
  return {
    ...state,
    isLoading: false,
    error: null,
  }
};
const onCompatibleModulesFailed = (state, payload) => {
  return {
    ioModuleCategories: [],
    comModuleCategories: [],
    cartridgeCategories: [],
    ioModulesFailedRules: [],
    comModulesFailedRules: [],
    cartridgesFailedRules: [],
    ioModulesDevicesFailedRules: {},
    comModulesDevicesFailedRules: {},
    cartridgesDevicesFailedRules: {},
    canAddExtensionRack: false,
    isLoading: false,
    error: null,
  }
};

export const getCompatibleModulesStarted = createAction('GET_COMPATIBLE_MODULES_STARTED');
export const getCompatibleModulesSuccess = createAction('GET_COMPATIBLE_MODULES_SUCCESS');
export const getCompatibleModulesSuccessWithoutUpdate = createAction('GET_COMPATIBLE_MODULES_SUCCESS_WITHOUT_UPDATE');
export const getCompatibleModulesFailed = createAction('GET_COMPATIBLE_MODULES_FAILED');

export const getCompatibleModulesReducer = createReducer(function (on, off) {
  on(getCompatibleModulesStarted, onCompatibleModulesStarted);
  on(getCompatibleModulesSuccess, onCompatibleModulesSuccess);
  on(getCompatibleModulesSuccessWithoutUpdate, onCompatibleModulesSuccessWithoutUpdate);
  on(getCompatibleModulesFailed, onCompatibleModulesFailed);
}, DEFAULT_STATE_COMPATIBLE_MODULES);

export const getCompatibleIslandModulesStarted = createAction('GET_COMPATIBLE_ISLAND_MODULES_STARTED');
export const getCompatibleIslandModulesSuccess = createAction('GET_COMPATIBLE_ISLAND_MODULES_SUCCESS');
export const getCompatibleIslandModulesSuccessWithoutUpdate = createAction('GET_COMPATIBLE_ISLAND_MODULES_SUCCESS_WITHOUT_UPDATE');
export const getCompatibleIslandModulesFailed = createAction('GET_COMPATIBLE_ISLAND_MODULES_FAILED');

export const getCompatibleIslandModulesReducer = createReducer(function (on, off) {
  on(getCompatibleIslandModulesStarted, onCompatibleModulesStarted);
  on(getCompatibleIslandModulesSuccess, onCompatibleModulesSuccess);
  on(getCompatibleIslandModulesSuccessWithoutUpdate, onCompatibleModulesSuccessWithoutUpdate);
  on(getCompatibleIslandModulesFailed, onCompatibleModulesFailed);
}, DEFAULT_STATE_COMPATIBLE_MODULES);

export const getCompatibleModules = (configurationID, criteria, shouldUpdate, specificIslandSlot = 0) => {
  let dispatchActions = {
    'started': getCompatibleModulesStarted,
    'success': getCompatibleModulesSuccess,
    'successWithoutUpdate': getCompatibleModulesSuccessWithoutUpdate,
    'failed': getCompatibleModulesFailed,
  };
  const params = {};
  if (specificIslandSlot >= 1) {
    params.island = specificIslandSlot;
    dispatchActions = {
      'started': getCompatibleIslandModulesStarted,
      'success': getCompatibleIslandModulesSuccess,
      'successWithoutUpdate': getCompatibleIslandModulesSuccessWithoutUpdate,
      'failed': getCompatibleIslandModulesFailed,
    };
  }
  const paramsStr = Object.entries(params).map(([key, value]) => `&${key}=${value}`);
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const payload = {
    criteria: { ...criteria, },
    distributorUnscoping: appConfiguration.distributorUnscoping,
    allowPrecommercialization: appConfiguration.allowPrecommercialization
  };

  return async dispatch => {
    dispatch(dispatchActions.started());
    return API_INSTANCE.post(`${api_base_url}/configurations/${configurationID}/next-modules?locale=${getAppLocale()}${paramsStr}${mainOverrideString}`,
      payload)
      .then((success) => {
        const payload = success?.data?.payload || {};

        const ioModuleCategories = (payload?.ioModules?.categories || []).map(cData => new ProductCategory(cData || {}));
        const comModuleCategories = (payload?.comModules?.categories || []).map(cData => new ProductCategory(cData || {}));
        const cartridgeCategories = (payload?.cartridges?.categories || []).map(cData => new ProductCategory(cData || {}));
        const allCategories = [
          ...ioModuleCategories,
          ...comModuleCategories,
          ...cartridgeCategories,
        ];

        const allReferences = [];
        allCategories.forEach(c => allReferences.push(...(c.references)));
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));

        if (shouldUpdate) {
          dispatch(dispatchActions.success(payload));
        } else {
          dispatch(dispatchActions.successWithoutUpdate());
        }

        return payload;
      }).catch((error) => {
        dispatch(dispatchActions.failed());
        throw error;
      });

  }
}

/* Replaceable modules retrieval */
const DEFAULT_STATE_REPLACEABLE_MODULES = {
  isLoading: false,
  error: null,
};

export const getReplaceableModulesStarted = createAction('GET_REPLACEABLE_MODULES_STARTED');
export const getReplaceableModulesSuccess = createAction('GET_REPLACEABLE_MODULES_SUCCESS');
export const getReplaceableModulesFailed = createAction('GET_REPLACEABLE_MODULES_FAILED');

export const getReplaceableModulesReducer = createReducer(function (on, off) {
  on(getReplaceableModulesStarted, (state, payload) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    }
  });
  on(getReplaceableModulesSuccess, (state, payload) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    }
  });

  on(getReplaceableModulesFailed, (state, payload) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE_REPLACEABLE_MODULES);


export const getReplaceableModules = (configurationID, criteria, replacementSlot, moduleType, specificIslandSlot = 0, specificLocalRack = null) => {
  const params = {};
  if (specificIslandSlot >= 1) {
    params.island = specificIslandSlot;
  }
  if (specificLocalRack >= 1) {
    params.localRack = specificLocalRack;
  }
  const paramsStr = Object.entries(params).map(([key, value]) => `&${key}=${value}`).join('');
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return async dispatch => {
    dispatch(getReplaceableModulesStarted());
    return API_INSTANCE.post(`${api_base_url}/configurations/${configurationID}/replaceable-modules?locale=${getAppLocale()}${paramsStr}${mainOverrideString}`,
      {
        replacementSlot,
        moduleType,
        criteria: { ...criteria, },
        distributorUnscoping: appConfiguration.distributorUnscoping,
        allowPrecommercialization: appConfiguration.allowPrecommercialization
      })
      .then((success) => {
        const payload = success?.data?.payload || {};
        const categories = (payload?.categories || []).map(cData => new ProductCategory(cData || {}));

        const allReferences = [];
        categories.forEach(c => allReferences.push(...(c.references)));
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));

        dispatch(getReplaceableModulesSuccess());

        return payload;
      }).catch((error) => {
        dispatch(getReplaceableModulesFailed());
        throw error;
      });
  }
}

/* Insertable modules retrieval */
const DEFAULT_STATE_INSERTABLE_MODULES = {
  isLoading: false,
  devicesFailedRules: {},
  error: null
};

export const getInsertableModulesStarted = createAction('GET_INSERTABLE_MODULES_STARTED');
export const getInsertableModulesSuccess = createAction('GET_INSERTABLE_MODULES_SUCCESS');
export const getInsertableModulesFailed = createAction('GET_INSERTABLE_MODULES_FAILED');

export const getInsertableModulesReducer = createReducer(function (on, off) {
  on(getInsertableModulesStarted, (state, payload) => {

    return {
      ...state,
      devicesFailedRules: {},
      isLoading: true,
      error: null,
    }
  });
  on(getInsertableModulesSuccess, (state, payload) => {
    return {
      ...state,
      devicesFailedRules: payload.map(c => c.failedRulesForReferences).reduce((previous, current) => {
        return { ...previous, ...current };
      }, {}),
      isLoading: false,
      error: null,
    }
  });

  on(getInsertableModulesFailed, (state, payload) => {
    return {
      ...state,
      devicesFailedRules: {},
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE_INSERTABLE_MODULES);


export const getInsertableModules = (configurationID, criteria, insertionSlot, moduleType, incrementSlot, specificIslandSlot = 0, specificLocalRack = 0) => {

  const params = {};
  if (specificIslandSlot >= 1) {
    params.island = specificIslandSlot;
  }

  if (specificLocalRack >= 1) {
    params.localRack = specificLocalRack;
  }
  let paramsStr = Object.entries(params).map(([key, value]) => `&${key}=${value}`).join('');
  paramsStr += `&incrementSlot=${incrementSlot}`
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';

  return async dispatch => {
    dispatch(getInsertableModulesStarted());
    return API_INSTANCE.post(`${api_base_url}/configurations/${configurationID}/insertable-modules?locale=${getAppLocale()}${paramsStr}${mainOverrideString}`,
      {
        insertionSlot,
        moduleType,
        criteria: { ...criteria, },
        distributorUnscoping: appConfiguration.distributorUnscoping,
        allowPrecommercialization: appConfiguration.allowPrecommercialization
      })
      .then((success) => {
        const payload = success?.data?.payload || {};
        const categories = (payload?.categories || []).map(cData => new ProductCategory(cData || {}));

        const allReferences = [];
        categories.forEach(c => allReferences.push(...(c.references)));
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));

        dispatch(getInsertableModulesSuccess(categories));

        return payload;
      }).catch((error) => {
        dispatch(getInsertableModulesFailed());
        throw error;
      });
  }
}

/* Configuration compatible BC for IO island retrieval */

const DEFAULT_STATE_COMPATIBLE_ISLAND_BC = {
  isLoading: false,
  error: null
};

export const getCompatibleIslandBCStarted = createAction('COMPATIBLE_ISLAND_BC_STARTED');
export const getCompatibleIslandBCSuccess = createAction('COMPATIBLE_ISLAND_BC_SUCCESS');
export const getCompatibleIslandBCFailed = createAction('COMPATIBLE_ISLAND_BC_FAILED');

export const getCompatibleIslandBCReducer = createReducer(function (on, off) {
  on(getCompatibleIslandBCStarted, (state, payload) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    }
  });
  on(getCompatibleIslandBCSuccess, (state, payload) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    }
  });

  on(getCompatibleIslandBCFailed, (state, payload) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE_COMPATIBLE_ISLAND_BC);


export const getCompatibleIslandBC = (configurationID, criteria) => {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return async dispatch => {
    dispatch(getCompatibleIslandBCStarted());
    return API_INSTANCE.post(`${api_base_url}/configurations/${configurationID}/compatible-island-bc?locale=${getAppLocale()}${mainOverrideString}`,
      {
        criteria: { ...criteria, },
        distributorUnscoping: appConfiguration.distributorUnscoping
      })
      .then((success) => {
        const payload = success?.data?.payload || {};
        const categories = (payload?.categories || []).map(cData => new ProductCategory(cData || {}));

        const allReferences = [];
        categories.forEach(c => allReferences.push(...(c.references)));
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));

        dispatch(getCompatibleIslandBCSuccess());

        return payload;
      }).catch((error) => {
        dispatch(getCompatibleIslandBCFailed());
        throw error;
      });
  }
}

export const getCompatibleRemoteAreaRIO = (configurationID, criteria) => {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return async dispatch => {
    dispatch(getCompatibleIslandBCStarted());
    return API_INSTANCE.post(`${api_base_url}/configurations/${configurationID}/compatible-remote-area-rio?locale=${getAppLocale()}${mainOverrideString}`,
      {
        criteria: { ...criteria, },
        distributorUnscoping: appConfiguration.distributorUnscoping
      })
      .then((success) => {
        const payload = success?.data?.payload || {};
        const categories = (payload?.categories || []).map(cData => new ProductCategory(cData || {}));

        const allReferences = [];
        categories.forEach(c => allReferences.push(...(c.references)));
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));

        dispatch(getCompatibleIslandBCSuccess());

        return payload;
      }).catch((error) => {
        dispatch(getCompatibleIslandBCFailed());
        throw error;
      });
  }
}


/* Configuration compatible BC for IO island retrieval */

const DEFAULT_STATE_REPLACEABLE_HEAD = {
  isLoading: false,
  error: null
};

export const getReplaceableHeadStarted = createAction('REPLACEABLE_HEAD_STARTED');
export const getReplaceableHeadSuccess = createAction('REPLACEABLE_HEAD_SUCCESS');
export const getReplaceableHeadFailed = createAction('REPLACEABLE_HEAD_FAILED');

export const getReplaceableHeadReducer = createReducer(function (on, off) {
  on(getReplaceableHeadStarted, (state, payload) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    }
  });
  on(getReplaceableHeadSuccess, (state, payload) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    }
  });

  on(getReplaceableHeadFailed, (state, payload) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE_REPLACEABLE_HEAD);


export const getReplaceableHead = (configurationID, criteria, specificIslandSlot = 0) => {
  const params = {};
  if (specificIslandSlot >= 1) {
    params.island = specificIslandSlot;
  }
  const paramsStr = Object.entries(params).map(([key, value]) => `&${key}=${value}`);
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';


  return async (dispatch, getState) => {
    dispatch(getReplaceableHeadStarted());

    const payload = {
      criteria: { ...criteria, },
      distributorUnscoping: appConfiguration.distributorUnscoping
    };

    const rangesToScope = getState().callByRangeReducer?.selectedItems.ranges ? Object.keys(getState().callByRangeReducer?.selectedItems.ranges) : [];
    const referencesToScope = getState().callByRangeReducer?.selectedItems.references ? Object.keys(getState().callByRangeReducer?.selectedItems.references) : [];

    if (rangesToScope.length > 0) {
      payload.scopedCategories = rangesToScope;
    }

    if (getState().callByRangeReducer?.selectedItems.references) {
      payload.scopedReferences = referencesToScope;

    }


    return API_INSTANCE.post(`${api_base_url}/configurations/${configurationID}/replaceable-head?locale=${getAppLocale()}${paramsStr}${mainOverrideString}`,
      payload)
      .then((success) => {
        const payload = success?.data?.payload || {};
        const categories = (payload?.categories || []).map(cData => new ProductCategory(cData || {}));

        const allReferences = [];
        categories.forEach(c => allReferences.push(...(c.references)));
        ProductInformationCache.loadReferencesDescriptions(allReferences.map(r => r.id));

        dispatch(getReplaceableHeadSuccess());

        return payload;
      }).catch((error) => {
        dispatch(getReplaceableHeadFailed());
        throw error;
      });
  }
}

/* Pacdrive kits retrieval */

const DEFAULT_STATE_PACDRIVE_KITS = {
  kits: {},
  isLoading: false,
  error: null
};

export const getPacdriveKitsStarted = createAction('GET_PACDRIVE_KITS_STARTED');
export const getPacdriveKitsSuccess = createAction('GET_PACDRIVE_KITS_SUCCESS');
export const getPacdriveKitsFailed = createAction('GET_PACDRIVE_KITS_FAILED');

export const getPacdriveKitsReducer = createReducer(function (on, off) {
  on(getPacdriveKitsStarted, (state, payload) => {
    return {
      ...state,
      kits: {},
      isLoading: true,
      error: null,
    }
  });
  on(getPacdriveKitsSuccess, (state, payload) => {
    return {
      ...state,
      kits: payload,
      isLoading: false,
      error: null,
    }
  });

  on(getPacdriveKitsFailed, (state, payload) => {
    return {
      ...state,
      kits: {},
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE_PACDRIVE_KITS);

export const getPacdriveKits = () => {

  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return async dispatch => {
    dispatch(getPacdriveKitsStarted());
    return API_INSTANCE.get(`${api_base_url}/pacdrive-kits?locale=${getAppLocale()}${mainOverrideString}`)
      .then((success) => {
        const payload = success?.data?.payload || {};
        const kitsMap = (payload?.kits || {});

        const allReferences = [];
        Object.keys(kitsMap).forEach(c => allReferences.push(c));
        ProductInformationCache.loadReferencesDescriptions(allReferences);

        dispatch(getPacdriveKitsSuccess(kitsMap));

        return kitsMap;
      }).catch((error) => {
        dispatch(getPacdriveKitsFailed());
      });
  }
}



